import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";

import supabase from "../supabase/supabase";

const ClientContext = createContext(null);

/* =========================================================
   CONSTANTS
========================================================= */

const INVITATION_STORAGE_KEY =
  "profityo_pending_client_invitation";

const FIRM_TYPES = [
  "accounting_firm",
  "accountant_practice",
];

const CLIENT_ROLES = [
  "owner",
  "owner_member",
  "client",
  "client_member",
];

const FIRM_ROLES = [
  "owner",
  "owner_member",
];

/* =========================================================
   HELPERS
========================================================= */

const getErrorMessage = (error, fallback) =>
  typeof error?.message === "string" &&
  error.message.trim()
    ? error.message
    : fallback;

const getRows = (data) => {
  if (Array.isArray(data)) return data;

  return data && typeof data === "object"
    ? [data]
    : [];
};

const isValidInvitationToken = (value) =>
  typeof value === "string" &&
  /^[0-9a-f]{64}$/i.test(value);

const clientStorageKey = (userId) =>
  `activeClientId:${userId}`;

const workspaceStorageKey = (userId) =>
  `activeWorkspaceId:${userId}`;

const readStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.warn(
      "Unable to save workspace selection:",
      error
    );
  }
};

const readSavedClientId = (userId) =>
  readStorage(clientStorageKey(userId));

const saveClientId = (userId, clientId) =>
  writeStorage(
    clientStorageKey(userId),
    clientId
  );

const readSavedWorkspaceId = (userId) =>
  readStorage(workspaceStorageKey(userId));

const saveWorkspaceId = (userId, workspaceId) =>
  writeStorage(
    workspaceStorageKey(userId),
    workspaceId
  );

const getClientFirmId = (row) =>
  row.managing_organization_id ??
  row.managing_org_id ??
  row.account_id ??
  null;

/* =========================================================
   WORKSPACE NORMALIZATION
========================================================= */

const mapWorkspace = (row) => {
  const organizationType =
    row.organization_type;

  const workspaceType =
    FIRM_TYPES.includes(organizationType)
      ? "firm"
      : organizationType === "client"
        ? "client"
        : null;

  if (!workspaceType) return null;

  const role = row.membership_role;

  const validRole =
    workspaceType === "firm"
      ? FIRM_ROLES.includes(role)
      : CLIENT_ROLES.includes(role);

  if (!validRole) return null;

  if (
    !row.organization_id ||
    !row.organization_name
  ) {
    return null;
  }

  return {
    id: row.organization_id,
    organization_id: row.organization_id,

    name: row.organization_name,
    organization_name:
      row.organization_name,

    type: organizationType,
    organization_type:
      organizationType,

    workspaceType,
    workspace_type: workspaceType,

    role,
    membership_role: role,

    country: row.country ?? null,
    currency: row.currency ?? "USD",

    isFirm: workspaceType === "firm",
    isClient: workspaceType === "client",

    created_at: row.created_at ?? null,
  };
};

const mapFirm = (workspace) => ({
  id: workspace.id,
  name: workspace.name,
  type: workspace.type,
  role: workspace.role,
  currency: workspace.currency,
  country: workspace.country,
});

const mapDirectClient = (workspace) => ({
  id: workspace.id,

  account_id: null,

  name: workspace.name,
  legal_name: workspace.name,

  type: "client",
  isClient: true,

  role: workspace.role,
  status: "active",

  currency: workspace.currency,
  billingCurrency: null,

  hasDirectMembership: true,
});

const mapHubClient = (
  row,
  currenciesById,
  directWorkspaceById
) => {
  const id = row.organization_id;
  const directWorkspace =
    directWorkspaceById.get(id);

  return {
    id,

    account_id: getClientFirmId(row),

    name: row.name,
    legal_name: row.name,

    type: "client",
    isClient: true,

    /*
     * The client hub RPC can return a
     * managing firm's role.
     *
     * If this user also has direct client
     * membership, prefer that direct role.
     */
    role:
      directWorkspace?.role ??
      row.membership_role ??
      null,

    status: row.status ?? "active",

    currency:
      directWorkspace?.currency ??
      currenciesById[id] ??
      null,

    billingCurrency:
      row.account_currency ?? null,

    hasDirectMembership:
      Boolean(directWorkspace),
  };
};

/* =========================================================
   CLIENT PROVIDER
========================================================= */

export function ClientProvider({ children }) {
  /* -------------------------------------------------------
     AUTHENTICATION
  ------------------------------------------------------- */

  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  const [
    isAuthenticated,
    setIsAuthenticated,
  ] = useState(false);

  const [isLoading, setIsLoading] =
    useState(true);

  const [
    isPasswordRecovery,
    setIsPasswordRecovery,
  ] = useState(false);

  /* -------------------------------------------------------
     EXISTING FIRM AND CLIENT STATE
  ------------------------------------------------------- */

  const [firms, setFirms] = useState([]);
  const [activeFirm, setActiveFirm] =
    useState(null);

  const [clients, setClients] = useState([]);
  const [activeClient, setActiveClient] =
    useState(null);

  /* -------------------------------------------------------
     NEW WORKSPACE STATE
  ------------------------------------------------------- */

  const [
    availableWorkspaces,
    setAvailableWorkspaces,
  ] = useState([]);

  const [
    activeWorkspace,
    setActiveWorkspace,
  ] = useState(null);

  const [
    workspaceError,
    setWorkspaceError,
  ] = useState(null);

  const workspaceType =
    activeWorkspace?.workspaceType ?? null;

  const firmWorkspaces =
    availableWorkspaces.filter(
      (workspace) =>
        workspace.workspaceType === "firm"
    );

  const clientWorkspaces =
    availableWorkspaces.filter(
      (workspace) =>
        workspace.workspaceType === "client"
    );

  const hasFirmAccess =
    firmWorkspaces.length > 0;

  const hasClientAccess =
    clientWorkspaces.length > 0;

  /* -------------------------------------------------------
     REQUEST CONTROL
  ------------------------------------------------------- */

  const currentUserIdRef = useRef(null);

  const loadVersionRef = useRef(0);
  const authGenerationRef = useRef(0);

  const profileLoadRef = useRef({
    userId: null,
    promise: null,
  });

  const workspaceLoadedForRef =
    useRef(null);

  const workspaceSnapshotRef =
    useRef(null);

  /* =======================================================
     CLEAR WORKSPACE
  ======================================================= */

  const clearWorkspace = useCallback(() => {
    loadVersionRef.current += 1;

    workspaceLoadedForRef.current = null;
    workspaceSnapshotRef.current = null;

    profileLoadRef.current = {
      userId: null,
      promise: null,
    };

    setProfile(null);

    setFirms([]);
    setActiveFirm(null);

    setClients([]);
    setActiveClient(null);

    setAvailableWorkspaces([]);
    setActiveWorkspace(null);

    setWorkspaceError(null);
  }, []);

  /* =======================================================
     CLEAR AUTHENTICATION
  ======================================================= */

  const clearAuthentication = useCallback(() => {
    currentUserIdRef.current = null;

    clearWorkspace();

    setSession(null);
    setUser(null);

    setIsAuthenticated(false);
    setIsPasswordRecovery(false);
  }, [clearWorkspace]);

  /* =======================================================
     LOAD VERIFIED WORKSPACE DATA
  ======================================================= */

  const fetchProfileAndAccount = useCallback(
    (userId, force = false) => {
      if (
        !userId ||
        currentUserIdRef.current !== userId
      ) {
        return Promise.resolve({
          success: false,
          error: "Authentication required.",
        });
      }

      /*
       * Reuse a running request for the
       * same user unless force=true.
       */

      if (
        !force &&
        profileLoadRef.current.userId ===
          userId &&
        profileLoadRef.current.promise
      ) {
        return profileLoadRef.current.promise;
      }

      /*
       * Return the cached result, including
       * data, rather than {success:true}
       * without workspace information.
       */

      if (
        !force &&
        workspaceLoadedForRef.current ===
          userId &&
        workspaceSnapshotRef.current
      ) {
        return Promise.resolve({
          success: true,
          data: workspaceSnapshotRef.current,
        });
      }

      const requestVersion =
        ++loadVersionRef.current;

      const isCurrentRequest = () =>
        loadVersionRef.current ===
          requestVersion &&
        currentUserIdRef.current === userId;

      const superseded = () => ({
        success: false,
        error: "Workspace request superseded.",
      });

      const promise = (async () => {
        try {
          if (isCurrentRequest()) {
            setWorkspaceError(null);
          }

          /* -----------------------------------------------
             1. CANONICAL PROFILE
          ----------------------------------------------- */

          const {
            data: profileData,
            error: profileError,
          } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", userId)
            .maybeSingle();

          if (!isCurrentRequest()) {
            return superseded();
          }

          if (profileError) {
            throw new Error(
              `Unable to load your profile: ${
                profileError.message
              }`
            );
          }

          if (!profileData) {
            throw new Error(
              "Your user profile was not found."
            );
          }

          /* -----------------------------------------------
             2. DIRECT MEMBERSHIPS
             Source of truth:
             public.get_my_workspaces()
          ----------------------------------------------- */

          const {
            data: workspaceRows,
            error: workspaceRpcError,
          } = await supabase.rpc(
            "get_my_workspaces"
          );

          if (!isCurrentRequest()) {
            return superseded();
          }

          if (workspaceRpcError) {
            throw new Error(
              `Unable to load your workspaces: ${
                workspaceRpcError.message
              }`
            );
          }

          const workspaces = getRows(
            workspaceRows
          )
            .map(mapWorkspace)
            .filter(Boolean);

          /*
           * Membership can legitimately be
           * empty immediately after invited-client
           * signup, before invitation acceptance.
           */

          const directFirmWorkspaces =
            workspaces.filter(
              (workspace) =>
                workspace.workspaceType ===
                "firm"
            );

          const directClientWorkspaces =
            workspaces.filter(
              (workspace) =>
                workspace.workspaceType ===
                "client"
            );

          const mappedFirms =
            directFirmWorkspaces.map(
              mapFirm
            );

          /* -----------------------------------------------
             3. RESTORE AUTHORIZED WORKSPACE
          ----------------------------------------------- */

          const savedWorkspaceId =
            readSavedWorkspaceId(userId);

          const selectedWorkspace =
            workspaces.find(
              (workspace) =>
                workspace.id ===
                savedWorkspaceId
            ) ||
            workspaces[0] ||
            null;

          const selectedFirm =
            selectedWorkspace?.workspaceType ===
            "firm"
              ? mappedFirms.find(
                  (firm) =>
                    firm.id ===
                    selectedWorkspace.id
                ) || null
              : mappedFirms[0] || null;

          /*
           * A firm owner may manage clients
           * without having direct membership
           * in those client organizations.
           *
           * Those managed clients are separate
           * from the workspace selector.
           */

          /* -----------------------------------------------
             4. MANAGED CLIENT HUB
          ----------------------------------------------- */

          const {
            data: organizationRows,
            error: organizationError,
          } = await supabase.rpc(
            "get_client_hub_organizations_v2"
          );

          if (!isCurrentRequest()) {
            return superseded();
          }

          if (organizationError) {
            throw new Error(
              `Unable to load client organizations: ${
                organizationError.message
              }`
            );
          }

          const hubClientRows = getRows(
            organizationRows
          ).filter(
            (organization) =>
              organization.is_client === true &&
              organization.type ===
                "client" &&
              Boolean(
                organization.organization_id
              )
          );

          /* -----------------------------------------------
             5. CLIENT CURRENCIES
             Supplemental lookup.
          ----------------------------------------------- */

          const directWorkspaceById =
            new Map(
              directClientWorkspaces.map(
                (workspace) => [
                  workspace.id,
                  workspace,
                ]
              )
            );

          const organizationIds = [
            ...new Set(
              hubClientRows.map(
                (organization) =>
                  organization.organization_id
              )
            ),
          ];

          let currenciesById = {};

          if (
            organizationIds.length > 0
          ) {
            const {
              data: currencyRows,
              error: currencyError,
            } = await supabase
              .schema("accounting")
              .from("organizations")
              .select("id,currency")
              .in(
                "id",
                organizationIds
              );

            if (!isCurrentRequest()) {
              return superseded();
            }

            if (currencyError) {
              console.warn(
                "Unable to load client currencies:",
                currencyError
              );
            } else {
              currenciesById =
                Object.fromEntries(
                  (currencyRows || []).map(
                    (organization) => [
                      organization.id,
                      organization.currency ??
                        null,
                    ]
                  )
                );
            }
          }

          /* -----------------------------------------------
             6. BUILD CLIENT LIST
          ----------------------------------------------- */

          const mergedClients = new Map();

          /*
           * Begin with managed clients from
           * the existing client hub RPC.
           */

          hubClientRows.forEach(
            (organization) => {
              const client =
                mapHubClient(
                  organization,
                  currenciesById,
                  directWorkspaceById
                );

              mergedClients.set(
                client.id,
                client
              );
            }
          );

          /*
           * Add directly authorized client
           * workspaces that are missing from
           * the managed client hub.
           *
           * This supports invited clients even
           * if the hub query changes later.
           */

          directClientWorkspaces.forEach(
            (workspace) => {
              if (
                !mergedClients.has(
                  workspace.id
                )
              ) {
                mergedClients.set(
                  workspace.id,
                  mapDirectClient(
                    workspace
                  )
                );
              }
            }
          );

          const clientList = Array.from(
            mergedClients.values()
          );

          /* -----------------------------------------------
             7. RESTORE CLIENT SELECTION
          ----------------------------------------------- */

          const savedClientId =
            readSavedClientId(userId);

          /*
           * When entering a direct client
           * workspace, that organization must
           * be the active client.
           *
           * When entering a firm workspace,
           * preserve the selected managed
           * client if it is still authorized.
           */

          const selectedClientId =
            selectedWorkspace?.workspaceType ===
            "client"
              ? selectedWorkspace.id
              : savedClientId;

          const selectedClient =
            clientList.find(
              (client) =>
                client.id ===
                selectedClientId
            ) ||
            clientList[0] ||
            null;

          if (!isCurrentRequest()) {
            return superseded();
          }

          /* -----------------------------------------------
             8. COMMIT AUTHORIZED STATE
          ----------------------------------------------- */

          setProfile(profileData);

          setAvailableWorkspaces(
            workspaces
          );

          setActiveWorkspace(
            selectedWorkspace
          );

          setFirms(mappedFirms);
          setActiveFirm(selectedFirm);

          setClients(clientList);
          setActiveClient(selectedClient);

          saveWorkspaceId(
            userId,
            selectedWorkspace?.id || null
          );

          saveClientId(
            userId,
            selectedClient?.id || null
          );

          /*
           * Zero memberships is not automatically
           * an error. New invited clients can
           * have no membership until acceptance.
           */

          workspaceLoadedForRef.current =
            userId;

          const result = {
            profile: profileData,
            availableWorkspaces:
              workspaces,
            activeWorkspace:
              selectedWorkspace,
            workspaceType:
              selectedWorkspace?.workspaceType ??
              null,

            firmWorkspaces:
              directFirmWorkspaces,

            clientWorkspaces:
              directClientWorkspaces,

            hasFirmAccess:
              directFirmWorkspaces.length >
              0,

            hasClientAccess:
              directClientWorkspaces.length >
              0,

            firms: mappedFirms,
            activeFirm: selectedFirm,

            clients: clientList,
            activeClient:
              selectedClient,
          };

          workspaceSnapshotRef.current =
            result;

          setWorkspaceError(null);

          return {
            success: true,
            data: result,
          };
        } catch (error) {
          if (!isCurrentRequest()) {
            return superseded();
          }

          console.error(
            "Workspace initialization failed:",
            error
          );

          const message =
            getErrorMessage(
              error,
              "Unable to initialize your workspace."
            );

          workspaceLoadedForRef.current =
            null;

          workspaceSnapshotRef.current =
            null;

          setWorkspaceError(message);

          return {
            success: false,
            error: message,
          };
        }
      })();

      profileLoadRef.current = {
        userId,
        promise,
      };

      void promise.finally(() => {
        if (
          profileLoadRef.current.promise ===
          promise
        ) {
          profileLoadRef.current = {
            userId: null,
            promise: null,
          };
        }
      });

      return promise;
    },
    []
  );

  /* =======================================================
     AUTHENTICATION INITIALIZATION
  ======================================================= */

  useEffect(() => {
    let mounted = true;

    const processSession = async (
      nextSession,
      generation
    ) => {
      if (
        !mounted ||
        authGenerationRef.current !==
          generation
      ) {
        return;
      }

      if (!nextSession?.user) {
        clearAuthentication();
        setIsLoading(false);
        return;
      }

      const nextUserId =
        nextSession.user.id;

      const previousUserId =
        currentUserIdRef.current;

      if (
        previousUserId &&
        previousUserId !==
          nextUserId
      ) {
        clearWorkspace();
        setIsPasswordRecovery(false);
      }

      currentUserIdRef.current =
        nextUserId;

      setSession(nextSession);
      setUser(nextSession.user);

      setIsAuthenticated(true);

      const needsWorkspaceLoad =
        workspaceLoadedForRef.current !==
        nextUserId;

      if (!needsWorkspaceLoad) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        await fetchProfileAndAccount(
          nextUserId
        );
      } finally {
        if (
          mounted &&
          authGenerationRef.current ===
            generation
        ) {
          setIsLoading(false);
        }
      }
    };

    /* -----------------------------------------------
       RESTORE INITIAL SESSION
    ----------------------------------------------- */

    const initialize = async () => {
      const generation =
        authGenerationRef.current;

      try {
        const {
          data,
          error,
        } = await supabase.auth.getSession();

        if (
          !mounted ||
          authGenerationRef.current !==
            generation
        ) {
          return;
        }

        if (error) throw error;

        await processSession(
          data?.session || null,
          generation
        );
      } catch (error) {
        if (
          mounted &&
          authGenerationRef.current ===
            generation
        ) {
          console.error(
            "Unable to restore authentication:",
            error
          );

          clearAuthentication();
          setIsLoading(false);
        }
      }
    };

    /* -----------------------------------------------
       AUTH STATE LISTENER
    ----------------------------------------------- */

    const { data: authState } =
      supabase.auth.onAuthStateChange(
        (event, currentSession) => {
          if (
            !mounted ||
            event === "INITIAL_SESSION"
          ) {
            return;
          }

          if (
            event === "TOKEN_REFRESHED" &&
            currentSession?.user?.id ===
              currentUserIdRef.current
          ) {
            queueMicrotask(() => {
              if (!mounted) return;

              setSession(
                currentSession
              );
              setUser(
                currentSession.user
              );
            });

            return;
          }

          if (
            event === "USER_UPDATED" &&
            currentSession?.user?.id ===
              currentUserIdRef.current
          ) {
            queueMicrotask(() => {
              if (!mounted) return;

              setSession(
                currentSession
              );
              setUser(
                currentSession.user
              );
            });

            return;
          }

          const generation =
            ++authGenerationRef.current;

          queueMicrotask(() => {
            if (
              !mounted ||
              authGenerationRef.current !==
                generation
            ) {
              return;
            }

            if (
              event === "PASSWORD_RECOVERY"
            ) {
              setIsPasswordRecovery(
                true
              );
            }

            void processSession(
              currentSession,
              generation
            );
          });
        }
      );

    void initialize();

    return () => {
      mounted = false;

      authState.subscription.unsubscribe();
    };
  }, [
    clearAuthentication,
    clearWorkspace,
    fetchProfileAndAccount,
  ]);

  /* =======================================================
     LOGIN
  ======================================================= */

  const login = async (
    email,
    password
  ) => {
    try {
      const {
        data,
        error,
      } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (
        error ||
        !data?.session
      ) {
        return {
          success: false,
          error:
            "Email or password is incorrect. Please try again.",
        };
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(
          error,
          "Unable to sign in."
        ),
      };
    }
  };

  /* =======================================================
     ACCOUNTANT / BOOKKEEPER / FIRM SIGNUP
  ======================================================= */

  const signup = async (
    registration
  ) => {
    try {
      const firstName =
        (registration.firstName ||
          "").trim();

      const lastName =
        (registration.lastName ||
          "").trim();

      const workspaceName =
        (registration.workspaceName ||
          "").trim();

      const phone =
        (registration.phone ||
          "").trim();

      const ownerType =
        registration.ownerType ||
        "accounting_firm";

      if (!workspaceName) {
        return {
          success: false,
          error:
            "Please enter your firm or business name.",
        };
      }

      if (
        ![
          "accountant",
          "bookkeeper",
          "accounting_firm",
        ].includes(ownerType)
      ) {
        return {
          success: false,
          error:
            "Please select a valid account type.",
        };
      }

      const fullName = [
        firstName,
        lastName,
      ]
        .filter(Boolean)
        .join(" ");

      const slug = workspaceName
        .toLowerCase()
        .replace(
          /[^a-z0-9]+/g,
          "-"
        )
        .replace(
          /^-|-$/g,
          ""
        );

      const {
        data,
        error,
      } = await supabase.auth.signUp({
        email:
          registration.email
            .trim()
            .toLowerCase(),

        password:
          registration.password,

        options: {
          data: {
            full_name: fullName,
            first_name: firstName,
            last_name: lastName,
            phone,

            workspace_name:
              workspaceName,

            workspace_slug: slug,

            user_type: ownerType,
            owner_type: ownerType,
          },
        },
      });

      if (error) {
        return {
          success: false,
          error: getErrorMessage(
            error,
            "Unable to create your account."
          ),
        };
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(
          error,
          "Unable to create your account."
        ),
      };
    }
  };

  /* =======================================================
     INVITED CLIENT SIGNUP
  ======================================================= */

  const signupInvitedClient = async (
    registration
  ) => {
    try {
      const firstName =
        (registration.firstName ||
          "").trim();

      const lastName =
        (registration.lastName ||
          "").trim();

      const email =
        (registration.email ||
          "")
          .trim()
          .toLowerCase();

      const phone =
        (registration.phone ||
          "").trim();

      const password =
        registration.password ||
        "";

      const invitationToken =
        registration.invitationToken ||
        "";

      if (
        !firstName ||
        !lastName
      ) {
        return {
          success: false,
          error:
            "First and last name are required.",
        };
      }

      if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          email
        )
      ) {
        return {
          success: false,
          error:
            "Enter a valid email address.",
        };
      }

      if (
        !isValidInvitationToken(
          invitationToken
        )
      ) {
        return {
          success: false,
          error:
            "A valid client invitation is required.",
        };
      }

      if (
        password.length < 8 ||
        !/[a-z]/.test(password) ||
        !/[A-Z]/.test(password) ||
        !/[0-9]/.test(password)
      ) {
        return {
          success: false,
          error:
            "Password must have at least 8 characters with uppercase, lowercase, and a number.",
        };
      }

      const fullName = [
        firstName,
        lastName,
      ].join(" ");

      /*
       * Keep the invitation in
       * same-tab sessionStorage.
       *
       * Never store the token in
       * Supabase user metadata.
       */

      try {
        sessionStorage.setItem(
          INVITATION_STORAGE_KEY,
          invitationToken
        );
      } catch {
        // Storage may be unavailable.
      }

      /*
       * Do not provide workspace_name,
       * workspace_slug, or owner_type.
       *
       * The signup trigger should not
       * create a firm for invited clients.
       */

      const {
        data,
        error,
      } = await supabase.auth.signUp({
        email,
        password,

        options: {
          emailRedirectTo:
            `${window.location.origin}/`,

          data: {
            full_name: fullName,
            first_name: firstName,
            last_name: lastName,
            phone,

            user_type:
              "invited_client",

            registration_source:
              "client_invitation",
          },
        },
      });

      if (error) {
        return {
          success: false,
          error: getErrorMessage(
            error,
            "Unable to create your client account."
          ),
        };
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(
          error,
          "Unable to create your client account."
        ),
      };
    }
  };

  /* =======================================================
     PASSWORD RECOVERY
  ======================================================= */

  const resetPassword = async (
    email
  ) => {
    try {
      const redirectTo =
        `${window.location.origin}${window.location.pathname}`;

      const {
        error,
      } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        { redirectTo }
      );

      if (error) {
        return {
          success: false,
          error:
            "Unable to send recovery instructions. Please try again.",
        };
      }

      return {
        success: true,
      };
    } catch {
      return {
        success: false,
        error:
          "Unable to send recovery instructions. Please try again.",
      };
    }
  };

  const updatePassword = async (
    password
  ) => {
    try {
      if (
        !isPasswordRecovery ||
        !session
      ) {
        return {
          success: false,
          error:
            "No active recovery session. Please request a new password reset link.",
        };
      }

      const {
        error,
      } = await supabase.auth.updateUser({
        password,
      });

      if (error) {
        return {
          success: false,
          error: getErrorMessage(
            error,
            "Unable to update your password."
          ),
        };
      }

      const {
        error: signOutError,
      } = await supabase.auth.signOut({
        scope: "local",
      });

      if (signOutError) {
        return {
          success: false,
          error:
            "Password updated, but sign-out failed. Please sign out manually.",
        };
      }

      setIsPasswordRecovery(false);

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(
          error,
          "Unable to complete password recovery."
        ),
      };
    }
  };

  /* =======================================================
     LOGOUT
  ======================================================= */

  const logout = async () => {
    setIsLoading(true);

    try {
      const {
        error,
      } = await supabase.auth.signOut({
        scope: "local",
      });

      if (error) {
        return {
          success: false,
          error: getErrorMessage(
            error,
            "Unable to sign out."
          ),
        };
      }

      authGenerationRef.current += 1;

      clearAuthentication();

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(
          error,
          "Unable to sign out."
        ),
      };
    } finally {
      setIsLoading(false);
    }
  };

  /* =======================================================
     SWITCH WORKSPACE — NEW
  ======================================================= */

  const switchWorkspace = async (
    organizationId
  ) => {
    const userId =
      currentUserIdRef.current;

    if (!userId) {
      return {
        success: false,
        error:
          "Authentication required.",
      };
    }

    /*
     * Only allow switching to workspaces
     * returned by the direct-membership RPC.
     */

    const workspace =
      availableWorkspaces.find(
        (item) =>
          item.id === organizationId
      );

    if (!workspace) {
      return {
        success: false,
        error:
          "This organization is not in your authorized workspace list.",
      };
    }

    /*
     * Keep existing activeFirm and activeClient
     * compatible with the selected workspace.
     */

    const nextFirm =
      workspace.workspaceType === "firm"
        ? firms.find(
            (firm) =>
              firm.id === workspace.id
          ) || null
        : firms[0] || null;

    const nextClient =
      workspace.workspaceType === "client"
        ? clients.find(
            (client) =>
              client.id === workspace.id
          ) || null
        : activeClient &&
            clients.some(
              (client) =>
                client.id ===
                activeClient.id
            )
          ? activeClient
          : clients[0] || null;

    if (
      workspace.workspaceType === "client" &&
      !nextClient
    ) {
      return {
        success: false,
        error:
          "Unable to locate the authorized client organization.",
      };
    }

    setActiveWorkspace(workspace);

    setActiveFirm(nextFirm);
    setActiveClient(nextClient);

    saveWorkspaceId(
      userId,
      workspace.id
    );

    saveClientId(
      userId,
      nextClient?.id || null
    );

    workspaceSnapshotRef.current = {
      ...workspaceSnapshotRef.current,

      activeWorkspace: workspace,
      workspaceType:
        workspace.workspaceType,

      activeFirm: nextFirm,
      activeClient: nextClient,
    };

    return {
      success: true,
      data: workspace,
    };
  };

  /* =======================================================
     SWITCH CLIENT — EXISTING API
  ======================================================= */

  const switchClient = async (id) => {
    const userId =
      currentUserIdRef.current;

    if (!userId) {
      return {
        success: false,
        error:
          "Authentication required.",
      };
    }

    const selectedClient =
      clients.find(
        (client) =>
          client.id === id
      );

    if (!selectedClient) {
      return {
        success: false,
        error:
          "Client is not in your authorized client list.",
      };
    }

    /*
     * This selects a client for accounting
     * management and reporting.
     *
     * It does NOT automatically change
     * the user's main workspace.
     *
     * To enter a personal client workspace,
     * use switchWorkspace(clientId).
     */

    setActiveClient(
      selectedClient
    );

    saveClientId(
      userId,
      selectedClient.id
    );

    workspaceSnapshotRef.current = {
      ...workspaceSnapshotRef.current,

      activeClient:
        selectedClient,
    };

    return {
      success: true,
      data: selectedClient,
    };
  };

  /* =======================================================
     REFRESH WORKSPACE DATA
  ======================================================= */

  const refreshWorkspaces =
    useCallback(async () => {
      const userId =
        currentUserIdRef.current;

      if (!userId) {
        return {
          success: false,
          error:
            "Authentication required.",
        };
      }

      return fetchProfileAndAccount(
        userId,
        true
      );
    }, [
      fetchProfileAndAccount,
    ]);

  /*
   * Keep the legacy refreshProfile API.
   *
   * AcceptInvitation.jsx calls this after
   * the database grants client membership.
   */

  const refreshProfile =
    refreshWorkspaces;

  /* =======================================================
     ONBOARDING STATUS
  ======================================================= */

  const updateOnboardingStatus =
    async () => ({
      success: false,
      error:
        "Onboarding status is not stored in the current profiles schema. This workflow must use the current organization model.",
    });

  /* =======================================================
     PROVIDER VALUE
  ======================================================= */

  return (
    <ClientContext.Provider
      value={{
        /* Authentication */
        session,
        user,
        profile,

        isAuthenticated,
        isLoading,
        isPasswordRecovery,

        /* Existing firm state */
        firms,
        activeFirm,

        /* Existing client state */
        clients,
        activeClient,

        /* New universal workspaces */
        availableWorkspaces,
        activeWorkspace,
        workspaceType,

        firmWorkspaces,
        clientWorkspaces,

        hasFirmAccess,
        hasClientAccess,

        workspaceError,

        /* Authentication methods */
        login,
        signup,
        signupInvitedClient,
        logout,

        resetPassword,
        updatePassword,

        /* Workspace methods */
        switchWorkspace,
        refreshWorkspaces,

        /* Existing methods */
        switchClient,
        refreshProfile,

        updateOnboardingStatus,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}

/* =========================================================
   CONTEXT HOOK
========================================================= */

export function useClient() {
  const context =
    useContext(ClientContext);

  if (!context) {
    throw new Error(
      "useClient must be used inside ClientProvider."
    );
  }

  return context;
}